// Copyright (c) Codicent Inside AB. All rights reserved.
// This software and its associated documentation files (the "Software") are
// proprietary to Codicent Inside AB. Unauthorized copying of this file, via any
// medium is strictly prohibited. The Software is provided "AS IS", without
// warranty of any kind, express or implied, including but not limited to the
// warranties of merchantability, fitness for a particular purpose, and
// noninfringement. In no event shall the authors or copyright holders be liable
// for any claim, damages, or other liability, whether in an action of contract,
// tort, or otherwise, arising from, out of, or in connection with the Software
// or the use or other dealings in the Software.

import { BrowserRouter, Route, Routes } from "react-router";
import { lazy, Suspense, useEffect, useMemo, useRef } from "react"; // Add useMemo
import mermaid from "mermaid";
// import { useToaster } from "./hooks";
// import { Button } from "../codicent/components";
import { makeStyles, MessageBar, MessageBarActions, MessageBarBody, MessageBarTitle } from "@fluentui/react-components";
import {
  APP_NAME,
  APP_URL,
  DEFAULT_LANGUAGE,
  MODULE_FORMS,
  MODULE_QR,
  MODULE_SALES,
  MODULE_SEARCH,
  MODULE_SNAP,
  MODULE_VOICE,
  REDIRECT_URL,
} from "./constants";
// import { AppFrame, CrmPage } from "./pages";
// import { useAudioRecorder, useAuthState, useLocalization, useRealtimeVoiceAI, useTools } from "../codicent";
import {
  AppStateMachine,
  CodicentService,
  Button,
  useToaster,
  useLocalization,
  useCodicentApp,
  AppFrame,
  Login,
  ImageView,
  CrmPage,
  Sales,
  FormInvite,
  Snap,
  Logout,
  Search,
  FormAccept,
  VoiceIcon,
} from "codicent-app-sdk";
// import { createAppStateMachine } from "../codicent/utils/appState";
// import Login from "./pages/Login";
import Debug from "./pages/Debug";
// import DebugInfo from "./components/DebugInfo";
import { useAuth0 } from "@auth0/auth0-react";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import useReqtangleCodicent from "./hooks/useReqtangleCodicent.ts";
import { useCodicentStore } from "./stores/codicentStore.tsx";
import { useRenderMonitor } from "./hooks/useRenderTester.tsx";
import { Project } from "@/models/project.ts";

// Lazy load components
const Menu = lazy(() => import("./pages/Menu"));
const Log = lazy(() => import("./pages/Log"));
const Chat = lazy(() => import("./pages/Chat"));
// const Login = lazy(() => import("codicent-app-sdk").then(module => ({ default: module.Login })));
// const Sales = lazy(() => import("./pages/Sales"));
// const Snap = lazy(() => import("./pages/Snap"));
// const FormInvite = lazy(() => import("./pages/FormInvite"));
// const FormAccept = lazy(() => import("./pages/FormAccept"));
// const ImageView = lazy(() => import("./pages/ImageView"));
const Splash = lazy(() => import("./pages/Splash"));
const Compose = lazy(() => import("./pages/Compose"));
const BugReport = lazy(() => import("./pages/BugReport"));
// const Help = lazy(() => import("./pages/Help"));
// // const Logout = lazy(() => import("./components/Logout"));
// const DebugInfo = lazy(() => import("./components/DebugInfo"));
// const Purchase = lazy(() => import("./pages/Purchase"));
const Canvas = lazy(() => import("./pages/Canvas"));
// const EnvFormPage = lazy(() => import("./pages/EnvFormPage"));
const QrScan = lazy(() => import("./pages/QrScan"));
// const Search = lazy(() => import("./pages/Search"));

const Dashboard = lazy(() => import("./pages/dashboard/dashboard.tsx"));
const Overview = lazy(() => import("./pages/dashboard/view/overview.tsx"));
// const ViewSelector = lazy(() => import("./pages/dashboard/viewSelector.tsx"));
const AllProjects = lazy(() => import("./pages/dashboard/allProjects.tsx"));
const SimpleEdit = lazy(() => import("./pages/dashboard/view/edit-simple2.tsx"));
const EditBoxDemo = lazy(() => import("./pages/EditBoxDemo.tsx"));
const Export = lazy(() => import("./pages/Export.tsx"));

const useStyles = makeStyles({
  error: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  container: {
    width: "100%",
    minHeight: "100vh",
    overflow: "auto",
    position: "relative",
    touchAction: "pan-y", // Added to enable horizontal swipe detection
  },
});

// Add custom event type
interface LogEvent extends CustomEvent {
  detail: { message: string };
}

// Memoized CodicentAppState for Dashboard
// Instead of // import { CodicentAppState } from "codicent-app-sdk";
export interface MemoizedCodicentAppState {
  state: string;
  nickname: string;
  name: string;
  // email?: string;
  service: CodicentService;
  fixAppUrl: (url: string) => string;
  stateMachine: AppStateMachine;
}

// const MyLogin = () => {
//   const auth0 = useAuth0();
//   if (auth0.isLoading) {
//     return <>Laddar...</>;
//   }

//   return <Login />;
// }

// export const useCodicentApp = ({ auth0 }: { auth0: Auth0ContextInterface }) => {
//   return auth0;
// };

const App = () => {
  const auth0 = useAuth0();
  const renderStats = useRenderMonitor("App");
  console.log(`🔄 App render #${renderStats.renderCount} at ${new Date().toLocaleTimeString()}`);

  // Add debugging to track what's causing App renders
  const prevAuth0 = useRef(auth0);
  const prevAppState = useRef<any>(null);

  useEffect(() => {
    // Check if auth0 is causing renders
    if (prevAuth0.current !== auth0) {
      console.log("🚨 App: auth0 object changed!", {
        isLoading: { old: prevAuth0.current?.isLoading, new: auth0.isLoading },
        isAuthenticated: { old: prevAuth0.current?.isAuthenticated, new: auth0.isAuthenticated },
      });
    }
    prevAuth0.current = auth0;
  });

  const appState = useCodicentApp({ auth0 });

  useEffect(() => {
    // Check if appState from useCodicentApp is changing
    if (prevAppState.current && prevAppState.current !== appState) {
      // Deep check what's different - only log occasionally to reduce noise
      if (renderStats.renderCount % 5 === 0) {
        console.log("🚨 App: appState changed - sample of changes:");
        Object.keys(appState).forEach((key) => {
          if (prevAppState.current[key as keyof typeof appState] !== appState[key as keyof typeof appState]) {
            console.log(`  - Property "${key}" changed`);
          }
        });
      }
    }
    prevAppState.current = appState;
  });

  // REMOVE the memoizedAppStateForHook - just pass appState directly
  // Hooks must be called at the top level, not inside useMemo

  const {
    audio,
    service,
    state,
    nickname,
    name,
    error,
    errorType,
    fixAppUrl,
    isBusy,
    setAnonymous,
    html,
    setHtml,
    voice,
    script,
  } = appState;

  const { ToasterComponent } = useToaster();
  const { t } = useLocalization();

  const styles = useStyles();

  // Get projects list + codicent api update functions - pass appState directly
  const codicentData = useReqtangleCodicent(appState);

  // Memoize codicentData to prevent unnecessary rerenders
  const memoizedCodicentData = useMemo(() => {
    console.log("🔧 Memoizing codicentData - only changes when dependencies change");
    return codicentData;
  }, [
    codicentData?.projects?.length, // Only change when projects array length changes
    codicentData?.projects ? JSON.stringify(codicentData.projects.map((p) => ({ id: p.id, name: p.name }))) : null, // Or when project basic info changes
    codicentData?.isInitialLoad,
    codicentData?.loadingProjects,
    codicentData?.currentStateName,
  ]);

  // Memoize appState for Dashboard
  const memoizedAppState: MemoizedCodicentAppState = useMemo(() => {
    console.log("🔧 Memoizing appState - only changes when essential props change");
    return {
      state,
      nickname,
      name,
      service,
      fixAppUrl,
      stateMachine: appState.stateMachine, // Pass the state machine directly
      // Only include what Dashboard actually needs
    };
  }, [state, nickname, name, service, fixAppUrl]);

  // Save codicent backend data to local zustand store
  const setProjects = useCodicentStore((s) => s.setProjects);

  // Update store/state if we get new codicent backend data
  useEffect(() => {
    if (codicentData?.projects?.[0]) {
      setProjects(codicentData.projects as Project[]);
    }
  }, [codicentData.projects]);

  // const [isLoading, setIsLoading] = useState(true);
  // const [loadedProjects, setLoadedProjects] = useState<ProjectDefinition[]>([]);

  // useEffect(() => {
  //   const loadProjects = async () => {
  //     setIsLoading(true);
  //     try {
  //       // Wait for projects to be loaded
  //       await new Promise(resolve => setTimeout(resolve, 1000)); // Give time for projects to load
  //       setLoadedProjects(data.projects);
  //     } catch (error) {
  //       console.error("Error loading projects:", error);
  //     } finally {
  //       setIsLoading(false);
  //     }
  //   };
  //   loadProjects();
  // }, [data.projects]);

  // useEffect(() => {
  //   // Load and log Project 2 data
  //   const loadProjectData = async () => {
  //     const projectData = await data.loadProject("reqtgl-dev_proj1");
  //     console.log("Project 2 Data:", projectData);
  //   };
  //   loadProjectData();
  // }, [data]);

  // const projectsData = await data.loadProject("reqtgl-dev_proj1");
  // const projectsMenu = {
  //   projects: [
  //     {
  //       "id": 1,
  //       "name": "Lumon Project"
  //     },
  //     {
  //       "id": 2,
  //       "name": "Brew Project"
  //     }
  //   ]
  // }

  useEffect(() => {
    if (html && MODULE_VOICE) {
      // if current route is not /canvas, navigate to /canvas
      window.location.pathname = "/canvas";
    }
  }, [html]);

  useEffect(() => {
    if (name && voice) {
      voice.setUsername(name);
      const browserLanguage = navigator.language || DEFAULT_LANGUAGE || "en-US";
      const language = browserLanguage.split("-")[0];
      voice.setLanguage(language);
    }
  }, [voice, name]);

  useEffect(() => {
    mermaid.initialize({ startOnLoad: false });
    const handleLogEvent = (e: LogEvent) => {
      service
        .logAction(e.detail.message, nickname || "unknown")
        .then(() => { })
        .catch(console.warn);
    };
    window.addEventListener("log-event", handleLogEvent as EventListener);
    return () => {
      window.removeEventListener("log-event", handleLogEvent as EventListener);
    };
  }, [service, nickname]);

  useEffect(() => {
    if (state !== "needsPurchase" && window.location.pathname.startsWith("/purchase")) {
      window.location.replace("/");
    }
  }, [state]);

  useEffect(() => {
    const allowAnonymous =
      window.location.pathname.startsWith("/formaccept") || window.location.pathname.startsWith("/search");
    if (allowAnonymous) {
      setAnonymous(true);
      // sm.current.updateContext({ isAnonymous: true });
    }
  }, []);

  if (REDIRECT_URL && state === "hasAccess") {
    window.location.href = fixAppUrl(REDIRECT_URL);
    return null; // Prevent rendering the rest of the app
  }

  const allowAnonymous =
    window.location.pathname.startsWith("/formaccept") || window.location.pathname.startsWith("/search");
  // TODO: clean up app state detection below, and maybe move codicent creation, listing to a separate component (Welcome, or new)

  return (
    <NuqsAdapter>
      {/* <BrowserRouter basename={import.meta.env.DEV ? import.meta.env.VITE_MANIFEST_SCOPE : undefined}> */}
      <BrowserRouter basename={import.meta.env.VITE_MANIFEST_SCOPE}>
        {state === "error" && <Splash state={appState} />}
        {isBusy() && <Splash state={appState} />}
        {(state === "error" || state === "registered" || state === "authenticating") && <Splash state={appState} />}
        {state === "notAuthenticated" && !allowAnonymous && <Login state={appState} />}
        <div className={styles.container}>
          {/* Render Stats Debug Display - Remove in production */}
          {import.meta.env.DEV && state === "hasAccess" && (
            <div className="fixed bottom-12 left-72 bg-black/80 text-white p-2 rounded text-xs z-[9999] font-mono">
              <div>🔄 Total Renders: {renderStats.renderCount}</div>
              <div>⚡ Initial Avg: {renderStats.avgFrequency.toFixed(1)}/sec</div>
              <div className="border-t border-gray-600 mt-1 pt-1">
                <div className={renderStats.recentFrequency > 1 ? "text-yellow-400" : "text-green-400"}>
                  📊 Current: {renderStats.recentFrequency.toFixed(1)}/sec
                </div>
                <div>📈 Last minute: {renderStats.lastMinuteRenders} renders</div>
              </div>
              <div>⏱️ Runtime: {(renderStats.totalTime / 1000).toFixed(1)}s</div>
              <div
                className={`mt-1 pt-1 border-t border-gray-600 ${renderStats.isStable
                    ? "text-green-400"
                    : renderStats.recentFrequency > 3
                      ? "text-red-400"
                      : "text-yellow-400"
                  }`}
              >
                {renderStats.isStable
                  ? "✅ Stable"
                  : renderStats.recentFrequency > 3
                    ? "⚠️ High frequency!"
                    : "🔄 Active"}
              </div>
            </div>
          )}
          <ToasterComponent />
          {error && (
            <div className={styles.error}>
              <MessageBar intent="error">
                <MessageBarBody>
                  <MessageBarTitle>{errorType}</MessageBarTitle>
                  {error}
                </MessageBarBody>
                <MessageBarActions containerAction={<Button appearance="transparent" icon="DismissRegular" />}>
                  <Button
                    onClick={() => {
                      window.location.href = "./";
                    }}
                  >
                    Starta om
                  </Button>
                </MessageBarActions>
              </MessageBar>
            </div>
          )}
          <Suspense fallback={<>{t("Laddar...")}</>}>
            <Routes>
              {MODULE_FORMS && <Route path="/formaccept" element={<FormAccept />} />}
              {MODULE_FORMS && <Route path="/chat" element={<Chat state={appState} />} />}
              {MODULE_SEARCH && <Route path="/search" element={<Search state={appState} />} />}
              <Route path="/debug" element={<Debug />} />
              <Route
                path="/"
                element={
                  (state === "hasAccess" || state === "noAccess") &&
                  (APP_URL ? (
                    <AppFrame src={fixAppUrl(APP_URL)} title={APP_NAME} />
                  ) : (
                    <AllProjects codicentData={memoizedCodicentData} appState={memoizedAppState as MemoizedCodicentAppState} />
                  ))
                }
              ></Route>
              {state === "hasAccess" && (
                <>
                  {/* dashboard - Use memoized props */}
                  <Route
                    path="/:projectid"
                    element={
                      <Dashboard
                        codicentData={memoizedCodicentData}
                        appState={memoizedAppState as MemoizedCodicentAppState}
                      />
                    }
                  >
                    <Route index element={<Overview />} />
                    {/* <Route path=":menuitem" element={<ViewSelector />}>
                      <Route path=":subitem" element={<ViewSelector />}>
                        <Route path="tabs/:tabId" element={<ViewSelector />} />
                        <Route path="tabs" element={<ViewSelector />} />
                      </Route>
                      <Route path="tabs/:tabId" element={<ViewSelector />} />
                      <Route path="tabs" element={<ViewSelector />} />
                    </Route> */}
                    <Route path=":menuitem">
                      <Route path=":subitem">
                        <Route path="tabs/:tabId" />
                        <Route path="tabs" />
                      </Route>
                      <Route path="tabs/:tabId" />
                      <Route path="tabs" />
                    </Route>
                    <Route path="voice" element={voice ? <VoiceIcon voice={voice} /> : null} />
                  </Route>
                  {/* Export specification page */}
                  <Route path="/:projectid/export" element={<Export codicentData={memoizedCodicentData} />} />
                  <Route path="*" element={<div>Can't find what you are looking for.</div>} />

                  {/* projectsData - Use memoized data */}
                  <Route
                    path="/data"
                    element={
                      <div style={{ overflow: "auto", maxHeight: "100vh" }}>
                        <h1>Data</h1>
                        <pre>{JSON.stringify(memoizedCodicentData, null, 2)}</pre>
                      </div>
                    }
                  />
                  <Route path="/menu" element={<Menu state={appState} />} />
                  <Route path="/log" element={<Log state={appState} />} />
                  <Route path="/chat" element={<Chat state={appState} />} />
                  <Route path="/new" element={<Compose state={appState} />} />
                  <Route path="/bugreport" element={<BugReport state={appState} />} />
                  <Route
                    path="/canvas"
                    element={<Canvas html={html} onHtmlChange={setHtml} voice={voice} script={script} />}
                  />
                  {/* <Route path="/help" element={<Help />} /> */}
                  {MODULE_SALES && <Route path="/sales" element={<Sales state={appState} />} />}
                  {MODULE_SNAP && <Route path="/snap" element={<Snap state={appState} audio={audio} />} />}
                  {MODULE_FORMS && <Route path="/forminvite" element={<FormInvite state={appState} />} />}
                  {MODULE_SALES && <Route path="/crm" element={<CrmPage state={appState} />} />}
                  {MODULE_QR && <Route path="/qr" element={<QrScan state={appState} />} />}
                  <Route path="/image" element={<ImageView state={appState} />} />
                  {/* Demo/Test Routes */}
                  <Route
                    path="/demo/simple-edit"
                    element={
                      <SimpleEdit
                        data={{
                          type: "simpleEdit",
                          data: {
                            vision: "Sample vision statement for demo",
                            mission: "Sample mission statement for demo",
                            projectDescription: "Sample project description for demo",
                          },
                        }}
                        dataPath={["demo"]}
                        codicentData={memoizedCodicentData}
                      />
                    }
                  />
                  <Route path="/demo/editbox" element={<EditBoxDemo />} />
                  {/* <Route path="/debug" element={<DebugInfo state={appState} />} /> */}
                  {/* <Route path="/createapp" element={<EnvFormPage state={appState} />} /> */}
                  <Route
                    path="/app/*"
                    element={
                      <AppFrame
                        title={APP_NAME}
                        src={fixAppUrl(
                          `./app${window.location.pathname.replace(/^\/app/, "")}${window.location.search}`
                        )}
                        showFooter
                        scrollable
                      />
                    }
                  />
                </>
              )}
              <Route path="/logout" element={<Logout state={appState} />} />
              {/* {state === "needsPurchase" && <Route path="/purchase" element={<Purchase state={sm.current} />} />} */}
            </Routes>
          </Suspense>
        </div>
      </BrowserRouter>
    </NuqsAdapter>
  );
};

export default App;
